/**
 * Affiliates Tab
 *
 * Table of affiliates: code, user, rate, enabled toggle, validity, and balance.
 * Inline edit of rate / valid_until and enable/disable, persisted via PUT.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Switch } from '@/components/ui/Switch';
import { toast } from 'sonner';
import { useI18n } from '@/i18n';
import {
  affiliateApi,
  type AffiliateAdminItem,
  type AffiliateBalance,
  type UpdateAffiliatePayload,
} from '@/lib/api/affiliate';

function formatMoney(cents: number, currency: string): string {
  const symbol = currency === 'CNY' || currency === 'cny' ? '¥' : '¥';
  return `${symbol}${(cents / 100).toFixed(2)}`;
}

function isoToDateInput(iso?: string | null): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

function BalanceCell({
  balance,
  labels,
}: {
  balance: AffiliateBalance;
  labels: { withdrawable: string; pending: string; cleared: string; negative: string };
}) {
  return (
    <div className="space-y-0.5 text-xs">
      <div className={balance.negative_balance ? 'text-destructive font-semibold' : 'text-foreground'}>
        {labels.withdrawable}: {formatMoney(balance.withdrawable_cents, balance.currency)}
        {balance.negative_balance && (
          <span className="ml-1 text-destructive">({labels.negative})</span>
        )}
      </div>
      <div className="text-muted-foreground">
        {labels.pending}: {formatMoney(balance.pending_cents, balance.currency)}
      </div>
      <div className="text-muted-foreground">
        {labels.cleared}: {formatMoney(balance.cleared_cents, balance.currency)}
      </div>
    </div>
  );
}

export function AffiliatesTab() {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['affiliate-affiliates'],
    queryFn: affiliateApi.getAffiliates,
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editRate, setEditRate] = useState('');
  const [editValidUntil, setEditValidUntil] = useState('');

  const mutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateAffiliatePayload }) =>
      affiliateApi.updateAffiliate(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['affiliate-affiliates'] });
      toast.success(t('affiliatePage.affiliateUpdated'));
      setEditingId(null);
    },
    onError: (err: Error) => {
      toast.error(`${t('affiliatePage.actionFailed')}: ${err.message}`);
    },
  });

  const startEdit = (item: AffiliateAdminItem) => {
    setEditingId(item.id);
    setEditRate(String(item.commission_rate));
    setEditValidUntil(isoToDateInput(item.valid_until));
  };

  const saveEdit = (id: string) => {
    const parsedRate = editRate.trim() === '' ? undefined : Number(editRate);
    mutation.mutate({
      id,
      payload: {
        commission_rate: parsedRate,
        valid_until: editValidUntil ? new Date(editValidUntil).toISOString() : undefined,
      },
    });
  };

  const toggleEnabled = (item: AffiliateAdminItem, enabled: boolean) => {
    mutation.mutate({ id: item.id, payload: { enabled } });
  };

  const balanceLabels = {
    withdrawable: t('affiliatePage.balanceWithdrawable'),
    pending: t('affiliatePage.balancePending'),
    cleared: t('affiliatePage.balanceCleared'),
    negative: t('affiliatePage.negativeBalance'),
  };

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            {t('affiliatePage.affiliatesTitle')}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">{t('affiliatePage.affiliatesDesc')}</p>
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          {t('affiliatePage.refresh')}
        </Button>
      </div>

      {error && (
        <Card className="mb-4 border-destructive">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0" />
            <p className="text-destructive text-sm">{(error as Error).message}</p>
          </div>
        </Card>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      ) : !data?.length ? (
        <Card>
          <p className="text-center text-muted-foreground py-8">
            {t('affiliatePage.noAffiliates')}
          </p>
        </Card>
      ) : (
        <Card padding={false}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="px-4 py-2">{t('affiliatePage.colCode')}</th>
                  <th className="px-4 py-2">{t('affiliatePage.colUserId')}</th>
                  <th className="px-4 py-2">{t('affiliatePage.colRate')}</th>
                  <th className="px-4 py-2">{t('affiliatePage.colEnabled')}</th>
                  <th className="px-4 py-2">{t('affiliatePage.colValidity')}</th>
                  <th className="px-4 py-2">{t('affiliatePage.colBalance')}</th>
                  <th className="px-4 py-2 text-right">{t('affiliatePage.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item: AffiliateAdminItem) => {
                  const editing = editingId === item.id;
                  return (
                    <tr key={item.id} className="border-b border-border align-top">
                      <td className="px-4 py-3 font-mono text-foreground">{item.code}</td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs text-muted-foreground" title={item.user_id}>
                          {item.user_id}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {editing ? (
                          <div className="w-28">
                            <Input
                              type="number"
                              step="0.01"
                              min={0}
                              max={1}
                              value={editRate}
                              onChange={(e) => setEditRate(e.target.value)}
                            />
                          </div>
                        ) : (
                          <span className="text-foreground">
                            {(item.commission_rate * 100).toFixed(1)}%
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Switch
                          checked={item.enabled}
                          onChange={(checked) => toggleEnabled(item, checked)}
                          disabled={mutation.isPending}
                          ariaLabel={t('affiliatePage.colEnabled')}
                        />
                      </td>
                      <td className="px-4 py-3">
                        {editing ? (
                          <div className="w-40">
                            <Input
                              type="date"
                              value={editValidUntil}
                              onChange={(e) => setEditValidUntil(e.target.value)}
                            />
                          </div>
                        ) : (
                          <span className="text-muted-foreground whitespace-nowrap">
                            {item.valid_until
                              ? isoToDateInput(item.valid_until)
                              : t('affiliatePage.noExpiry')}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <BalanceCell balance={item.balance} labels={balanceLabels} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          {editing ? (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setEditingId(null)}
                              >
                                {t('affiliatePage.cancel')}
                              </Button>
                              <Button
                                variant="primary"
                                size="sm"
                                disabled={mutation.isPending}
                                onClick={() => saveEdit(item.id)}
                              >
                                {mutation.isPending
                                  ? t('affiliatePage.saving')
                                  : t('affiliatePage.save')}
                              </Button>
                            </>
                          ) : (
                            <Button variant="outline" size="sm" onClick={() => startEdit(item)}>
                              <Pencil className="w-4 h-4 mr-1" />
                              {t('affiliatePage.edit')}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
